import { motion } from "framer-motion";
import { FilmSlate, GameController, ForkKnife } from "@phosphor-icons/react";

const FAMILY = "/images/exp-family.webp";
const COUPLE = "/images/exp-couple.webp";

export default function Experience() {
  return (
    <section
      id="experience"
      data-testid="experience-section"
      className="relative bg-[#FAFAFA] py-24 md:py-32"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="mb-14 md:mb-20 max-w-3xl">
          <p className="overline text-zinc-500 mb-4">Your Friday, sorted</p>
          <h2 className="font-display font-extrabold text-zinc-900 text-4xl md:text-6xl tracking-tighter leading-[0.95]">
            Everyday moments, <br />
            <span className="gradient-text">made easier.</span>
          </h2>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
          {/* Large image left */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            data-testid="bento-family"
            className="md:col-span-7 relative rounded-3xl overflow-hidden aspect-[4/3] md:aspect-auto md:min-h-[520px]"
          >
            <img src={FAMILY} alt="Family dinner" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12 text-white">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur text-xs font-semibold uppercase tracking-wider mb-4">
                <ForkKnife size={14} weight="duotone" />
                Family nights
              </div>
              <h3 className="font-display font-extrabold text-3xl md:text-5xl tracking-tighter max-w-lg">
                One order. Everyone fed. No row.
              </h3>
            </div>
          </motion.div>

          {/* Stacked right */}
          <div className="md:col-span-5 grid grid-cols-1 gap-4 md:gap-6">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              data-testid="bento-couple"
              className="relative rounded-3xl overflow-hidden aspect-[5/4] md:aspect-auto md:min-h-[250px]"
            >
              <img src={COUPLE} alt="Date night" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-white">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur text-xs font-semibold uppercase tracking-wider mb-3">
                  Date nights
                </div>
                <h3 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight">
                  Skip the queue. Stay in the mood.
                </h3>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              data-testid="bento-expansion"
              className="relative rounded-3xl p-8 md:p-10 bg-[#0A0A0A] text-white overflow-hidden min-h-[250px]"
            >
              <div className="absolute -bottom-16 -right-16 h-64 w-64 rounded-full brand-gradient opacity-30 blur-3xl" />
              <p className="overline text-[#FF1F8B] mb-4">What's next</p>
              <h3 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight mb-3">
                More than takeaways.
              </h3>
              <p className="text-zinc-400 text-sm md:text-base leading-relaxed max-w-sm mb-5">
                Cinema. Arcade. The weekend. Quietly, we're working on the
                easiest way to book the rest of your evening, too.
              </p>
              <div className="flex items-center gap-3 text-zinc-500">
                <FilmSlate size={22} weight="duotone" />
                <span className="h-1 w-1 rounded-full bg-zinc-700" />
                <GameController size={22} weight="duotone" />
                <span className="h-1 w-1 rounded-full bg-zinc-700" />
                <span className="text-xs uppercase tracking-widest">More soon</span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
